import React from "react";
import image8 from "../../assets/image 8.png";
import commingSoonImage from "../../assets/coming-soon.svg";

const Icon = ({ name, className = "" }: { name: string; className?: string }) => (
  <span className={`material-icons ${className}`} aria-hidden>
    {name}
  </span>
);

type Item = { title: string; subtitle: string; icon: string };

const gettingStarted: Item[] = [
  { title: "Understanding Grants 101", subtitle: "A beginner’s guide to what grants are and how they work.", icon: "menu_book" },
  { title: "Eligibility Checklist", subtitle: "Key criteria to know before applying for your first grant.", icon: "checklist" },
  { title: "How to Write a Strong Proposal", subtitle: "Simple steps to create a compelling and structured grant application.", icon: "edit_note" },
  { title: "Using the Funderland Dashboard", subtitle: "Quick walkthrough on tracking, saving, and applying for grants.", icon: "dashboard" },
];

const fundraising: Item[] = [
  { title: "Building a Fundraising Strategy", subtitle: "How to set clear goals and design a roadmap for funding success.", icon: "insights" },
  { title: "Storytelling for Fundraising", subtitle: "Learn how to craft stories that inspire people to support your cause.", icon: "auto_stories" },
  { title: "Engaging Donors & Sponsors", subtitle: "Tips to connect with individuals and organizations that care about your mission.", icon: "diversity_3" },
  { title: "Startup Toolkit", subtitle: "Your Comprehensive guide to launching and scaling your  startup from incorporation to fundraising", icon: "workspaces" },
];

const isStartupToolkitEnabled = false; // Feature flag
export default function StartupToolkit() {
  return (
    <>
      {isStartupToolkitEnabled ? (
        <div className="mx-auto px-5 xl:px-7 py-6">
          {/* Page header */}
          <div className="mb-6">
            <h1 className="text-[32px] md:text-[32px] font-medium">Startup Toolkit</h1>
            <p className="text-md font-medium text-[#777B7F] mt-1">
              Your comprehensive guide to launching and scaling your startup from incorporation to fundraising
            </p>
          </div>

          {/* Search */}
          <div className="rounded-[12px] bg-white border border-[#E5E7EB] p-3 flex items-center gap-3 mb-6 shadow-sm">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-icons text-[19px] text-[#9AA3AF]">
                search
              </span>
              <input
                className="h-[40px] w-full rounded-[8px] border border-[#E5E7EB] pl-9 pr-3 outline-none text-[13.5px] placeholder:text-[#9AA3AF] focus:border-[#C7CBCF]"
                placeholder="Search by keyword, funder, or category…"
              />
            </div>

            <button className="h-[36px] px-4 rounded-lg bg-primary text-sm font-medium hover:brightness-95">
              Search
            </button>
          </div>

          {/* Sections */}
          <Section
            title="Getting Started"
            blurb="The first step to building your foundation for your venture"
            items={gettingStarted}
          />

          <Section
            title="Fundraising"
            blurb="The first step to building your foundation for your venture"
            items={fundraising}
          />

          {/* Bottom row */}
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            <ArticleCard />
            <ChecklistCard />
          </div>
        </div>
      ) : (
        <div className="mx-auto px-5 xl:px-7 py-6 bg-white h-full">
          <div className="flex flex-col items-center justify-center gap-2">
            <figure className="max-w-[100%] w-full md:max-w-[360px]">
              <img src={commingSoonImage} alt="coming soon" className="w-full" />
            </figure>
            <h1 className="text-[32px] md:text-[32px] font-medium text-[#f36120]">Startup Toolkit</h1>
            <p className="text-md font-medium text-[#777B7F] mt-1">
              This feature is currently unavailable.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

/* Pieces */

function Section({ title, blurb, items }: { title: string; blurb: string; items: Item[] }) {
  return (
    <div className="mb-6">
      <div className="px-2 md:px-0 mb-2">
        <div className="text-[18px] md:text-[20px] font-semibold">{title}</div>
        <div className="text-[12.5px] text-[#6B7280]">{blurb}</div>
      </div>

      <div className="space-y-3">
        {items.map((it) => (
          <Row key={it.title} item={it} />
        ))}
      </div>
    </div>
  );
}

function Row({ item }: { item: Item }) {
  return (
    <div className="rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm px-4 py-3 flex items-start justify-between">
      <div className="flex gap-3">
        <div className="h-9 w-9 rounded-full bg-[#E6F3EA] grid place-items-center flex-shrink-0">
          <Icon name={item.icon} className="text-[#5FA66A]" />
        </div>
        <div>
          <div className="text-md font-semibold text-[#0f172a]">{item.title}</div>
          <div className="text-sm text-[#777B7F] font-medium">{item.subtitle}</div>
        </div>
      </div>

      <button
        className="h-8 w-8 rounded-full bg-[#1F2C22] grid place-items-center text-white hover:brightness-110"
        aria-label="Open"
      >
        <span className="material-icons text-[19px] text-primary">chevron_right</span>
      </button>
    </div>
  );
}

function ArticleCard() {
  return (
    <div className="rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm overflow-hidden">
      <div className="aspect-[16/9] bg-[#EEF2F7]">
        <img
          src={image8}
          alt="img"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="p-4">
        <div className="text-sm text-[#6B7280]">John Deo · Sept 5, 2025</div>
        <div className="mt-1 text-2xl font-medium">Business Incorporate</div>

        <div className="mt-2 flex flex-wrap gap-2 text-sm">
          <Chip>Startup</Chip>
          <Chip>Business</Chip>
          <Chip>Education & Research</Chip>
        </div>

        <p className="mt-3 text-md text-[#777B7F] leading-relaxed">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer scelerisque porttitor bibendum.
          Aliquam dictum scelerisque nunc eu eleifend.
        </p>

        <button className="mt-3 h-[28px] px-3 rounded-[6px] border text-sm bg-primary hover:brightness-95">
          Read more
        </button>
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-[3px] rounded-[6px] bg-[#F3F4F6] border border-[#E5E7EB]">
      {children}
    </span>
  );
}

function ChecklistCard() {
  const [items, setItems] = React.useState<
    { label: string; checked: boolean }[]
  >([
    { label: "Define Your Project Goal", checked: false },
    { label: "Gather Essential Documents", checked: true },
    { label: "Research Eligible Grants", checked: false },
    { label: "Create a Submission Calendar", checked: false },
  ]);

  const toggle = (idx: number) =>
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, checked: !it.checked } : it))
    );

  const markAll = () =>
    setItems((prev) => prev.map((it) => ({ ...it, checked: true })));

  const done = items.filter((i) => i.checked).length;
  const pct = Math.round((done / items.length) * 100);

  return (
    <div className="rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm p-4">
      <div className="text-2xl font-medium mb-4">Checklist</div>

      <ul className="space-y-3">
        {items.map((it, i) => (
          <CheckRow
            key={it.label}
            label={it.label}
            checked={it.checked}
            onToggle={() => toggle(i)}
          />
        ))}
      </ul>

      {/* Progress */}
      <div className="mt-5">
        <div className="h-[6px] w-full rounded-full bg-[#E5E7EB] overflow-hidden">
          <div
            className="h-full bg-[#7CC68D] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-2 text-sm font-medium text-[#777B7F]">
          {done}/{items.length} Completed
        </div>
      </div>

      <button
        onClick={markAll}
        className="mt-4 h-[32px] w-full md:w-auto px-4 rounded-[6px] border border-primary text-sm hover:bg-black/[0.03]"
      >
        Mark All as Complete
      </button>
    </div>
  );
}

function CheckRow({
  label,
  checked = false,
  onToggle,
}: {
  label: string;
  checked?: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={onToggle}
        className="group w-full text-left select-none flex items-center gap-3"
      >
        <span
          className={`h-[18px] w-[18px] rounded-[4px] border grid place-items-center transition-colors ${checked
            ? "bg-[#1F2C22] border-[#1F2C22] text-white"
            : "border-[#C9CED3] bg-white group-hover:border-[#97A3AE]"
            }`}
        >
          {checked && <Icon name="check" className="text-[14px]" />}
        </span>
        <span className={`text-md text-[#777B7F] font-bold ${checked ? "line-through text-[#AEB2B2]" : ""} `}>{label}</span>
      </button>
    </li>
  );
}